import { formatDate } from "./format";
import type { Meeting, Milestone, Phase } from "../types";

export interface CalendarDay {
  date: Date;
  key: string;
  muted: boolean;
  weekStart: boolean;
  monthStart: boolean;
  gridStart: boolean;
}

export function calendarDateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function buildCalendarMonth(shown: Date): CalendarDay[] {
  const year = shown.getFullYear();
  const month = shown.getMonth();
  const first = new Date(year, month, 1);
  const leading = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cellCount = leading + daysInMonth <= 35 ? 35 : 42;
  return Array.from({ length: cellCount }, (_, index) => {
    const date = new Date(year, month, index - leading + 1);
    return {
      date,
      key: calendarDateKey(date),
      muted: date.getMonth() !== month,
      weekStart: index % 7 === 0,
      monthStart: date.getDate() === 1,
      gridStart: index === 0,
    };
  });
}

export function activePhasesOnDate(phases: Phase[], date: string) {
  return phases.filter(
    (phase) =>
      !phase.archived_at && phase.start_date <= date && phase.end_date >= date,
  );
}

export function formatPhaseDateRange(startDate: string, endDate: string) {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  return startDate === endDate ? start : `${start} – ${end}`;
}

export function phaseShowsLabel(phase: Phase, day: CalendarDay) {
  return phase.start_date === day.key || day.gridStart;
}

export type CalendarSelection =
  | { kind: "phase"; item: Phase }
  | { kind: "milestone"; item: Milestone }
  | { kind: "meeting"; item: Meeting };

export interface CalendarDetail {
  kindLabel: "FASE" | "HITO" | "REUNIÓN";
  title: string;
  date: string;
  time?: string;
  status?: string;
  description: string | null;
  link?: string | null;
}

const phaseStatusLabels = {
  PENDING: "Pendiente",
  IN_PROGRESS: "En curso",
  COMPLETED: "Completado",
} as const;

function meetingDateAndTime(meeting: Meeting) {
  if (!meeting.starts_at) {
    return {
      date: formatDate(meeting.date_window_start),
      time: "Hora por confirmar",
    };
  }
  const date = new Date(meeting.starts_at);
  const timeZone = meeting.timezone || "America/Mexico_City";
  try {
    return {
      date: new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone,
      })
        .format(date)
        .replace(".", ""),
      time: `${new Intl.DateTimeFormat("es-MX", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone,
      }).format(date)} · ${timeZone}`,
    };
  } catch {
    return {
      date: formatDate(meeting.starts_at.slice(0, 10)),
      time: "Hora por confirmar",
    };
  }
}

export function calendarSelectionDetail(
  selection: CalendarSelection,
): CalendarDetail {
  if (selection.kind === "phase") {
    return {
      kindLabel: "FASE",
      title: selection.item.name,
      date: formatPhaseDateRange(
        selection.item.start_date,
        selection.item.end_date,
      ),
      status: phaseStatusLabels[selection.item.status],
      description:
        selection.item.description.trim() || "Sin descripción adicional.",
    };
  }
  if (selection.kind === "milestone") {
    return {
      kindLabel: "HITO",
      title: selection.item.name,
      date: formatDate(selection.item.date),
      description: selection.item.description.trim() || null,
    };
  }
  const meetingTiming = meetingDateAndTime(selection.item);
  return {
    kindLabel: "REUNIÓN",
    title: selection.item.title,
    date: meetingTiming.date,
    time: meetingTiming.time,
    description:
      selection.item.objective.trim() ||
      selection.item.agenda.trim() ||
      selection.item.notes.trim() ||
      null,
    link: selection.item.meeting_url,
  };
}
